// src/components/institution/sections/AdmissionsSection.jsx
import React from 'react';
import { User, CheckCircle, Calendar, FileText, ChevronRight } from 'lucide-react';

export const AdmissionsSection = ({ data, settings, styling, onApplyClick }) => {
  const admissionSteps = settings?.admissionSteps || [
    {
      id: 1,
      title: 'Submit Application',
      description: 'Complete the online application form with required documents',
      icon: FileText
    },
    {
      id: 2,
      title: 'Application Review',
      description: 'Our admissions team reviews your application and documents',
      icon: User
    },
    {
      id: 3,
      title: 'Interview (if required)',
      description: 'Attend an interview session if required for your program',
      icon: Calendar
    },
    {
      id: 4,
      title: 'Admission Decision',
      description: 'Receive your admission decision and enrollment information',
      icon: CheckCircle
    }
  ];

  return (
    <section className="py-16 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: styling?.primaryColor || '#1e40af' }}
          >
            Admissions
          </h2>
          <div className="w-24 h-1 mx-auto mb-8" style={{ backgroundColor: styling?.primaryColor }} />
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Start your journey with us. Learn about our admission process and requirements.
          </p>
        </div>

        {/* Admission Process */}
        <div className="mb-12">
          <h3 className="text-2xl font-semibold text-gray-900 text-center mb-8">
            Admission Process
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {admissionSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={step.id} className="text-center">
                  <div className="relative mb-4">
                    <div 
                      className="w-16 h-16 mx-auto rounded-full flex items-center justify-center text-white"
                      style={{ backgroundColor: styling?.primaryColor || '#1e40af' }}
                    >
                      <Icon className="h-8 w-8" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-sm font-bold text-gray-600">
                      {step.id}
                    </div>
                    {index < admissionSteps.length - 1 && (
                      <div className="hidden lg:block absolute top-8 left-20 w-full h-0.5 bg-gray-300" />
                    )}
                  </div>
                  
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    {step.title}
                  </h4>
                  
                  <p className="text-gray-600 text-sm">
                    {step.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Requirements & Important Dates */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
          <div className="bg-gray-50 rounded-xl p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">
              Admission Requirements
            </h3>
            
            <ul className="space-y-3">
              {(settings?.admissionRequirements || [
                'Completed application form',
                'High school diploma or equivalent',
                'Official transcripts',
                'Letter of recommendation',
                'Personal statement',
                'Application fee payment'
              ]).map((requirement, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle 
                    className="h-5 w-5 mt-0.5 flex-shrink-0"
                    style={{ color: styling?.primaryColor || '#1e40af' }}
                  />
                  <span className="text-gray-600">{requirement}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-gray-50 rounded-xl p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">
              Important Dates
            </h3>
            
            <div className="space-y-4">
              {(settings?.importantDates || [
                { event: 'Application Deadline', date: 'March 31, 2025' },
                { event: 'Interview Period', date: 'April 1-15, 2025' },
                { event: 'Decision Notification', date: 'May 1, 2025' },
                { event: 'Enrollment Deadline', date: 'June 1, 2025' }
              ]).map((dateItem, index) => (
                <div key={index} className="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0">
                  <span className="font-medium text-gray-900">{dateItem.event}</span>
                  <span className="text-gray-600">{dateItem.date}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Apply Now CTA */}
        <div className="text-center">
          <button
            onClick={onApplyClick}
            className="text-white px-8 py-4 rounded-lg font-semibold text-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2 mx-auto"
            style={{ backgroundColor: styling?.primaryColor || '#1e40af' }}
          >
            Apply Now
            <ChevronRight className="h-5 w-5" />
          </button>
          
          <p className="text-gray-600 mt-4">
            Questions about admissions? <br/>
            Contact our admissions office for assistance.
          </p>
        </div>
      </div>
    </section>
  );
};